/**
 * The words of the guided tour (#1514), copied word for word from the approved
 * canvas boards (Main, P1 to P5 for the player; C0 to C3 for the coach), in the
 * ribbon's order (D411); the Dashboard card is added to them (#1560). Copy
 * only: the drawings live in `components/tour/TourArt.jsx`, the screen in
 * `pages/Tour.jsx`. Placeholders only (D208).
 *
 * `kind: "intro"` is the welcome card (three icon rows, no drawing); `"screen"`
 * is a card with a mini-screen drawing named by `art`. `last` is the closing
 * card: one gold action and a text link.
 */
export const PLAYER_TOUR = Object.freeze({
  role: "player",
  steps: Object.freeze([
    {
      kind: "intro",
      eyebrow: "A quick tour",
      skip: "Skip for now",
      title: "Here's how it works",
      lede: "Cricket Compass is your own cricket record. Three things to know before your first match goes in.",
      rows: [
        { icon: "trending-up", title: "Your record", body: "Log each match after the game. Your figures build from what you log, and nothing else." },
        { icon: "target", title: "Your plan", body: "The sentence you just wrote becomes goals you can check in on through the season." },
        { icon: "users", title: "Your coach, when you're ready", body: "Invite a coach and they can read your record. The account stays yours." },
      ],
      note: "You can take the tour again any time from Settings › More.",
      next: "Show me",
    },
    {
      kind: "screen",
      art: "record",
      label: "A small drawing of the Record Match screen",
      title: "Log a match",
      lede: "After a game, log it in three steps: where you played, your scorecard, and how it went. Your record and your figures build from what you log.",
      aside: "Got a game coming up? Plan it first, and finish the scorecard after the day.",
    },
    {
      kind: "screen",
      art: "dashboard",
      label: "A small drawing of the Dashboard screen",
      title: "Your dashboard",
      lede: "Your career figures, six charts and a map of the grounds you have played, all built from the matches you log. Pick a season to narrow the figures and charts.",
      aside: "Competition and team filters sit under More filters.",
    },
    {
      kind: "screen",
      art: "plan",
      label: "A small drawing of the Plan screen",
      title: "Your plan",
      lede: "The sentence you wrote at sign-up lives here. Turn it into goals with a target date, and check in on them as the season goes.",
      aside: "Coming up holds the matches you have planned, so the next one is never a surprise.",
    },
    {
      kind: "screen",
      art: "grounds",
      label: "A small drawing of the Grounds screen",
      title: "Grounds",
      lede: "The grounds near you, the ones you have played at, and what the pitch and the facilities were like. Every match you log is pinned to one.",
      aside: "A ground that is not on the map yet can be added right there, with its pin.",
    },
    {
      kind: "screen",
      art: "training",
      label: "A small drawing of the Training and guides screen",
      title: "Training & guides",
      lede: "Capture a training session and rate how it went from 1 to 5. Over a season that is the shape of your practice, next to your matches.",
      aside: "The guides are there for everyone. Drills and guides a coach assigns to you show under Assigned to you.",
    },
    {
      kind: "screen",
      art: "coach-access",
      label: "A small drawing of the coach access card in Settings",
      title: "Your coach, when you're ready",
      lede: "Either of you can send the invite; the other accepts. From then on your coach reads your record and can set you drills. You can end the link at any time.",
      aside: "The account is yours, from the first day. A coach never owns it.",
      last: true,
      primary: "Log your first match",
      secondary: "Go to my record instead",
    },
  ]),
});

export const COACH_TOUR = Object.freeze({
  role: "coach",
  steps: Object.freeze([
    {
      kind: "intro",
      eyebrow: "A quick tour for coaches",
      skip: "Skip for now",
      title: "Your players, their records",
      lede: "Cricket Compass runs on each player's own record. A player links you to theirs, and you coach from what they log.",
      rows: [
        { icon: "user-plus", title: "Invite", body: "Invite a player by email. They accept, and you are linked." },
        { icon: "eye", title: "Read", body: "Open a player: their record, plan, training and reflections, in one place." },
        { icon: "book-open", title: "Assign and plan", body: "Set drills and guides, leave feedback, schedule a game and pick the team." },
      ],
      note: "You can take the tour again any time from Settings › More.",
      next: "Show me",
    },
    {
      kind: "screen",
      art: "coach-invite",
      label: "A small drawing of the empty coach portal",
      title: "Invite a player",
      lede: "Type the player's email. They get the invite in their own app and accept it there. Until they do, you see nothing of theirs.",
      aside: "A player can invite you the same way. Either side can end the link later.",
    },
    {
      kind: "screen",
      art: "coach-player",
      label: "A small drawing of a player's page in the coach portal",
      title: "Read a player, then act",
      lede: "Open a player to see their matches, goals, training and reflections as they logged them. Write a drill once and assign it to one player or several; assign a guide the same way.",
      aside: "Feedback goes to the player. A private note stays with you.",
    },
    {
      kind: "screen",
      art: "coach-games",
      label: "A small drawing of the coach's games and team selection",
      title: "Your games",
      lede: "Schedule a game: where and when. Then pick the team from your players, and the game shows up on each of their plans.",
      aside: "Training sessions you schedule work the same way: the player adds how it went once it is done.",
      last: true,
      primary: "Invite your first player",
      secondary: "Go to Your players instead",
    },
  ]),
});

export const tourFor = (role) => (role === "coach" ? COACH_TOUR : PLAYER_TOUR);
